import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getHeatMapStatistics } from "../api/heat-map.api";
import type { StatisticModel } from "../types/Statistic";

function createHeatMapStatisticRequest() {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7;
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(startOfWeek);
  startOfNextWeek.setDate(startOfWeek.getDate() + 7);

  return { from: startOfWeek.toISOString(), to: startOfNextWeek.toISOString() };
}

export const useHeatMapStatistics = () => {
  const [data, setData] = useState<StatisticModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await runLatestRequest((signal) =>
        getHeatMapStatistics(createHeatMapStatisticRequest(), signal),
      );

      if (!result) return undefined;

      setData(result);
      return result;
    } catch (fetchError) {
      setData(null);
      setError(getApiErrorMessage(fetchError, "Failed to load dashboard statistics"));
      return undefined;
    }
  }, [runLatestRequest]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};
